import React, { useCallback, useEffect, useState } from 'react';
import { Table, Form, Spinner, Badge } from 'react-bootstrap';
import { toast } from 'react-toastify';
import api from '../../../api/apiClient';

const GlobalLearningScoreBadges = () => {
  const [activities, setActivities] = useState([]);
  const [badgeLevels, setBadgeLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.gamification.getConfig();
      if (!response.data?.success) {
        toast.error(response.data?.message || 'Failed to load configuration.');
        return;
      }
      setActivities(response.data.data?.activities || []);
      setBadgeLevels(response.data.data?.badge_levels || []);
    } catch (error) {
      toast.error(`Failed to load configuration: ${error.response?.data?.message || error.message}`);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const handleActivityUpdate = async (activity) => {
    setSavingId(`activity-${activity.id}`);
    try {
      await api.gamification.updateActivity(activity.id, {
        points: parseInt(activity.points, 10) || 0,
        status: parseInt(activity.status, 10) === 1 ? 1 : 0
      });
      toast.success('Learning activity updated.');
      await fetchConfig();
    } catch (error) {
      toast.error(`Failed to update activity: ${error.response?.data?.message || error.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const handleBadgeUpdate = async (badge) => {
    setSavingId(`badge-${badge.id}`);
    try {
      await api.gamification.updateBadgeLevel(badge.id, {
        minimum_points: parseInt(badge.minimum_points, 10) || 0,
        status: parseInt(badge.status, 10) === 1 ? 1 : 0
      });
      toast.success('Badge level updated.');
      await fetchConfig();
    } catch (error) {
      toast.error(`Failed to update badge level: ${error.response?.data?.message || error.message}`);
    } finally {
      setSavingId(null);
    }
  };

  const handleActivityChange = (id, field, value) => {
    setActivities((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  const handleBadgeChange = (id, field, value) => {
    setBadgeLevels((prev) => prev.map((item) => (item.id === id ? { ...item, [field]: value } : item)));
  };

  if (loading) {
    return (
      <div className="text-center py-16">
        <Spinner animation="border" className="text-purple-600" />
      </div>
    );
  }

  return (
    <div className="p-3 sm:p-4 md:p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-blue-50 min-h-screen">
      <h4 className="fw-bold mb-4 text-xl sm:text-2xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
        Global Learning Score &amp; Badges
      </h4>

      <div className="mb-5">
        <h5 className="font-bold text-gray-800 mb-3">Learning Activities</h5>
        <div className="overflow-x-auto rounded-2xl shadow-lg bg-white border border-purple-100">
          <Table bordered hover className="m-0 min-w-max text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3">Learning Activity</th>
                <th className="py-3 px-3">Points</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {activities.map((activity) => (
                <tr key={activity.id}>
                  <td className="py-3 px-3 font-medium">{activity.activity_name}</td>
                  <td className="py-3 px-3" style={{ width: 140 }}>
                    <Form.Control
                      type="number"
                      min="0"
                      value={activity.points}
                      onChange={(e) => handleActivityChange(activity.id, 'points', e.target.value)}
                    />
                  </td>
                  <td className="py-3 px-3" style={{ width: 140 }}>
                    <Form.Select
                      value={activity.status}
                      onChange={(e) => handleActivityChange(activity.id, 'status', e.target.value)}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </td>
                  <td className="py-3 px-3">
                    <button
                      type="button"
                      onClick={() => handleActivityUpdate(activity)}
                      disabled={savingId === `activity-${activity.id}`}
                      className="border border-purple-600 bg-white text-purple-600 rounded-lg px-3 py-1 text-sm font-semibold"
                    >
                      {savingId === `activity-${activity.id}` ? 'Saving...' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>

      <div>
        <h5 className="font-bold text-gray-800 mb-3">Badge Levels</h5>
        <div className="overflow-x-auto rounded-2xl shadow-lg bg-white border border-purple-100">
          <Table bordered hover className="m-0 min-w-max text-sm">
            <thead className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
              <tr>
                <th className="py-3 px-3">Badge Level</th>
                <th className="py-3 px-3">Badge</th>
                <th className="py-3 px-3">Minimum Points Required</th>
                <th className="py-3 px-3">Status</th>
                <th className="py-3 px-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {badgeLevels.map((badge) => (
                <tr key={badge.id}>
                  <td className="py-3 px-3 font-medium">{badge.level_name}</td>
                  <td className="py-3 px-3">
                    <Badge bg="light" text="dark" className="border">{badge.badge_label}</Badge>
                  </td>
                  <td className="py-3 px-3" style={{ width: 180 }}>
                    <Form.Control
                      type="number"
                      min="0"
                      value={badge.minimum_points}
                      onChange={(e) => handleBadgeChange(badge.id, 'minimum_points', e.target.value)}
                    />
                  </td>
                  <td className="py-3 px-3" style={{ width: 140 }}>
                    <Form.Select
                      value={badge.status}
                      onChange={(e) => handleBadgeChange(badge.id, 'status', e.target.value)}
                    >
                      <option value={1}>Active</option>
                      <option value={0}>Inactive</option>
                    </Form.Select>
                  </td>
                  <td className="py-3 px-3">
                    <button
                      type="button"
                      onClick={() => handleBadgeUpdate(badge)}
                      disabled={savingId === `badge-${badge.id}`}
                      className="border border-purple-600 bg-white text-purple-600 rounded-lg px-3 py-1 text-sm font-semibold"
                    >
                      {savingId === `badge-${badge.id}` ? 'Saving...' : 'Save'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    </div>
  );
};

export default GlobalLearningScoreBadges;
